// libs
import React, { Dispatch, SetStateAction, useState } from 'react';
import { useSelector } from 'react-redux';

//components
import Notify from 'shared/components/Notify/Mobile';
import { SpotLoader } from '@nykaa/ui-components/Loader';

// styles
import { ActionButton, Container } from './styles';
import { useWishlistContext } from 'shared/layouts/Wishlist/API/context';

//selectors
import { product as _product } from 'shared/store/wishlist/selectors';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

//contexts
import { useResponsiveContext } from 'shared/components/ResponsiveLoader/ResponsiveContext';

//constants
import { BREAKPOINTS } from 'shared/styles/base';
import { trackNotifyMeClicked } from 'shared/layouts/Wishlist/analytics';

// helpers
import NFA from 'shared/helpers/analytics/NFAnalytics';

// defs
interface IActionContainer {
  isOutOfStock: boolean;
  actionUrl: string;
  openInTab: boolean;
  productId: string;
  addToBagClickHandler: () => void;
  isLoading?: boolean;
  setShowSizeSelector: Dispatch<SetStateAction<boolean>>;
  isPriceRevealProduct: boolean | undefined;
  isSalePriceLive: boolean | undefined;
  isPriceDropped?: boolean;
  priceDropValue?: number;
}

const TEXT = {
  MOVE_TO_BAG: 'Move to Bag',
  NOTIFY: 'Notify Me',
};

function ActionContainer({
  isOutOfStock,
  productId,
  actionUrl,
  openInTab,
  addToBagClickHandler,
  isLoading = false,
  setShowSizeSelector,
  isPriceRevealProduct,
  isSalePriceLive = false,
  isPriceDropped = false,
  priceDropValue,
}: IActionContainer) {
  const [showNotify, setShowNotify] = useState<boolean>(false);
  const { addToCart } = useWishlistContext();

  const { deviceWidth } = useResponsiveContext();
  const isDesktop = deviceWidth >= BREAKPOINTS.DESKTOP;

  const { enabled: isDesktopSizeSelectorEnabled } = useRemoteConfig('sizeSelectedWishListDweb', {
    enabled: true,
  });

  const product = useSelector(_product(productId));

  const isOneSize = product?.type !== 'configurable';

  const desktopActionHandler = () => {
    if (isDesktopSizeSelectorEnabled) {
      if (isOneSize && !product?.isOutOfStock) {
        addToCart(product);
        return;
      }
      addToBagClickHandler();
      return;
    } else {
      if (openInTab) {
        window.open(actionUrl);
      }
    }
    return;
  };

  const onMoveToBagClicked = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    NFA.updateDataLayer({
      productType: product?.type,
      isPriceRevealProduct,
      isSalePriceLive,
      isPriceDropped,
      priceDropValue,
    });
    if (isDesktop) {
      return desktopActionHandler();
    }
    if (isOneSize) {
      addToCart(product);
      return;
    }

    setShowSizeSelector(true);
  };

  const desktopNotifyMeHandler = () => {
    if (isDesktopSizeSelectorEnabled) {
      return desktopActionHandler();
    }
    if (openInTab) {
      window.open(actionUrl);
      return;
    }
  };

  const onNotifyMeClicked = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    if (isDesktop) {
      return desktopNotifyMeHandler();
    }
    setShowNotify(true);
  };

  const handleOnNotified = () =>
    trackNotifyMeClicked({
      productId,
      price: product?.mrp,
      isOutOfStock: false,
      discountedPrice: product?.discountedPrice,
      discount: product?.discount,
      type: product?.type,
      categoryLevels: product?.primary_categories_json_all,
      isProductOffersAvailable: 'NA',
      productData: product,
    });

  return (
    <Container onClick={(e) => e.stopPropagation()}>
      {isOutOfStock ? (
        <ActionButton
          shape="default"
          kind="secondary"
          fullWidth
          size="medium"
          className="notifyMe"
          onClick={onNotifyMeClicked}
        >
          {TEXT.NOTIFY}
        </ActionButton>
      ) : (
        <ActionButton
          kind="secondary"
          fullWidth
          size="medium"
          shape="default"
          className="moveToBag"
          onClick={onMoveToBagClicked}
          data-at="move-to-bag"
        >
          {isLoading ? <SpotLoader /> : TEXT.MOVE_TO_BAG}
        </ActionButton>
      )}
      {showNotify && (
        <Notify
          isWishlist={true}
          showInlineform={false}
          productId={productId}
          onNotified={handleOnNotified}
          onHide={() => {
            setShowNotify(false);
          }}
        />
      )}
    </Container>
  );
}

export default ActionContainer;
